import { ApplicationRef, inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { IdleTimer, idleTimerFactory } from "src/app/support/idle";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { sntInfo } from "src/app/common/dialog";
import { sntTranslate } from "src/app/support/translate";
import { LockService } from "src/app/services/lock.service";
import { SessionRepository } from "src/app/repositories/session.repository";
import { Session } from "src/app/backend/models/session";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { getCustomerNominative } from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { getCurrentAccountNickName } from "src/app/core/account-nickname";
import { getCurrentDomainName } from "src/app/core/domain-name";

type IdleDetectionStatus = "enabled" | "disabled";

const SESSION_TIMEOUT = 1000 * 60 * 10;

@Injectable()
export class SessionService {
  private idleTimer: IdleTimer | undefined;

  private readonly lockService = inject(LockService);

  private readonly sessionRepository = new SessionRepository();

  private readonly applicationRef = inject(ApplicationRef);

  idleDetectionStatus: BehaviorSubject<IdleDetectionStatus> =
    new BehaviorSubject<IdleDetectionStatus>("disabled");

  create(): void {
    window.sessionStorage.setItem(
      "current-session",
      new Date().getTime().toString()
    );
  }

  isActive(): boolean {
    return !!window.sessionStorage.getItem("current-session");
  }

  delete(): void {
    window.sessionStorage.removeItem("current-session");
  }

  async activateIdleDetection(): Promise<void> {
    if (!this.idleTimer) {
      this.idleTimer = await idleTimerFactory(this.applicationRef);
    }

    if (
      await this.idleTimer?.activate(
        () => {
          this.lock();
        },
        SESSION_TIMEOUT,
        () => {}
      )
    ) {
      this.idleDetectionStatus.next("enabled");
    }
  }

  deactivateIdleDetection(): void {
    this.idleTimer?.deactivate();
    this.idleDetectionStatus.next("disabled");
  }

  async isIdlePermimssionGranted(): Promise<boolean> {
    if (!this.idleTimer) {
      this.idleTimer = await idleTimerFactory(this.applicationRef);
    }

    return (
      (await this.idleTimer?.isPermissionGranted()) || Promise.resolve(false)
    );
  }

  async canAskForPermission(): Promise<boolean> {
    if (!this.idleTimer) {
      this.idleTimer = await idleTimerFactory(this.applicationRef);
    }

    return (await this.idleTimer.permissionStatus()) === "prompt";
  }

  async notifyUserAbountIdlePermission(): Promise<void> {
    await sntInfo(
      sntTranslate(marker("Blocco automatico")),
      sntTranslate(
        marker(
          'Per poter attivare il blocco automatico è necessario autorizzare il browser. Successivamente a questo avviso apparirarà un messaggio di conferma in alto a sinistra, premi su "Consenti" per attivarlo.'
        )
      )
    );
  }

  async requestIdlePermission(): Promise<boolean> {
    if (!this.idleTimer) {
      this.idleTimer = await idleTimerFactory(this.applicationRef);
    }

    return await this.idleTimer.requestPermission();
  }

  isIdleMonitoringEnabled(): boolean {
    return this.idleDetectionStatus.value === "enabled";
  }

  isLocked(): boolean {
    return this.lockService.isLocked();
  }

  async lock(): Promise<void> {
    await this.lockService.lock();
  }

  async unLock(pin: string, session: Session): Promise<boolean> {
    const unLockResult = await this.lockService.unlock({
      pin,
      salt: session.salt,
      data: session.data,
      sessionId: session.id!,
      unlockLocation:
        session.url && session.url.indexOf("locked") >= 0
          ? `/${session.domainName}/${session.accountNickName}`
          : session.url || `/${session.domainName}/${session.accountNickName}`
    });

    if (unLockResult) {
      this.create();
    }

    return unLockResult;
  }

  list(): Promise<Session[]> {
    return this.sessionRepository.list();
  }

  async deleteByMemberId(memberId: string): Promise<void> {
    await this.sessionRepository.deleteByMemberId(memberId);
  }
}
